if (!customElements.get('tab-list')) {
  customElements.define(
    'tab-list',
    class TabList extends HTMLUListElement {
      constructor() {
        super();

        this.controls.forEach((button) => button.addEventListener('click', this.handleButtonClick.bind(this)));
      }

      get controls() {
        return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
      }

      handleButtonClick(event) {
        event.preventDefault();

        const target = event.currentTarget;
        const previousIndex = this.controls.findIndex((button) => button.getAttribute('aria-expanded') === 'true');
        const nextIndex = this.controls.indexOf(target);
        if (previousIndex === nextIndex) return;

        // Close any open cart-footer modal (note / discount / shipping /
        // gift wrap) before the panels move — since the tab push, their
        // overlay covers only the content area, so a switch would otherwise
        // carry an open modal away with the outgoing panel.
        this.closest('.drawer__inner')?.querySelectorAll('modal-element[open]').forEach((modal) => modal.hide());

        // Apple-style push: the outgoing panel stays rendered (open +
        // is-leaving, absolutely stacked) and slides out one side while the
        // incoming panel pushes in from the other — both on the pill
        // thumb's 0.35s clock. Skipped for reduced motion and for reset().
        const animate = !this._immediate
          && previousIndex !== -1
          && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const direction = nextIndex > previousIndex ? 'fwd' : 'back';

        this.controls.forEach((button, index) => {
          button.setAttribute('aria-expanded', 'false');

          const panel = document.getElementById(button.getAttribute('aria-controls'));
          if (!panel) return;

          if (animate && index === previousIndex) {
            panel.setAttribute('data-slide', direction);
            panel.classList.add('is-leaving');
            clearTimeout(panel._leaveTimer);
            panel._leaveTimer = setTimeout(() => {
              panel.classList.remove('is-leaving');
              panel.removeAttribute('open');
            }, 380);
          }
          else {
            clearTimeout(panel._leaveTimer);
            panel.classList.remove('is-leaving');
            panel.removeAttribute('open');
          }
        });

        target.setAttribute('aria-expanded', 'true');

        const panel = document.getElementById(target.getAttribute('aria-controls'));
        if (panel) {
          clearTimeout(panel._leaveTimer);
          panel.classList.remove('is-leaving');
          if (animate) {
            panel.setAttribute('data-slide', direction);
          }
          else {
            panel.removeAttribute('data-slide');
          }
          panel.setAttribute('open', '');
        }
      }

      reset() {
        this._immediate = true;
        const firstControl = this.controls[0];
        firstControl.dispatchEvent(new Event('click'));
        this._immediate = false;
      }
    }, { extends: 'ul' }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends DrawerElement {
      constructor() {
        super();

        this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
        this.onCartRefreshListener = this.onCartRefresh.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get shouldAppendToBody() {
        return false;
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return this.querySelector('[is="tab-list"]');
      }

      connectedCallback() {
        super.connectedCallback();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.addEventListener('cart:refresh', this.onCartRefreshListener);
        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener('is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
    
        document.removeEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.removeEventListener('cart:refresh', this.onCartRefreshListener);
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <p class="drawer__empty-text h2 heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</p>
          </div>
        </div>
        `;
      }

      async onCartRefresh(event) {
        const id = `MiniCart-${this.sectionId}`;
        if (document.getElementById(id) === null) return;

        const responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`)).text();
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;

        if (event.detail.open === true) {
          this.show();
        }
      }

      show(focusElement = null, animate = true) {
        super.show(focusElement, animate);

        if (this.tabList) {
          this.tabList.reset();

          if (this.open) {
            theme.a11y.trapFocus(this, this.focusElement);
          }
        }
      }
    }
  );
}

if (!customElements.get('swipe-remove')) {
  customElements.define(
    'swipe-remove',
    // iOS Clock-style slide-to-delete for cart drawer rows. Touch devices
    // only — on pointer devices the element is inert and the corner trash
    // badge (hidden here by CSS when the gesture is live) does the job.
    //
    // The gesture engages only on clear horizontal intent, so vertical cart
    // scrolling is untouched (touch-action: pan-y leaves that to the
    // browser). While dragging, --swipe-progress (0..1) drives the trash
    // disc's scale/fade in CSS — the "pop in" from the reference recording.
    // Deleting is a click on the row's OWN cart-remove anchor, so the
    // loader, collapse animation and cart re-render are all the existing
    // machinery.
    class SwipeRemove extends HTMLElement {
      static openInstance = null;

      constructor() {
        super();
        if (!theme.config.isTouch) return;

        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.onDocumentTouch = this.onDocumentTouch.bind(this);
      }

      connectedCallback() {
        if (!theme.config.isTouch) return;

        this.content = this.querySelector('.swipe-remove__content');
        this.action = this.querySelector('.swipe-remove__action');
        if (!this.content || !this.action) return;

        this.setAttribute('swipe-enabled', '');
        this.rtl = theme.config.rtl ? -1 : 1;

        this.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.addEventListener('touchmove', this.onTouchMove, { passive: true });
        this.addEventListener('touchend', this.onTouchEnd, { passive: true });
        this.addEventListener('touchcancel', this.onTouchEnd, { passive: true });

        this.action.addEventListener('click', () => {
          // Close FIRST: the forwarded click below passes through the same
          // capture guard, which must already see the row as closed.
          this.settle(0);
          const remove = this.querySelector('.cart-remove');
          if (remove) remove.click();
        });

        // A tap on the row while it is open closes it instead of following
        // the product link — the iOS behaviour. The disc and the remove
        // anchor are exempt: those clicks are the delete itself.
        this.addEventListener('click', (event) => {
          if (!this.offset) return;
          if (event.target.closest('.swipe-remove__action, .cart-remove')) return;
          event.preventDefault();
          event.stopPropagation();
          this.settle(0);
        }, true);
      }

      disconnectedCallback() {
        if (SwipeRemove.openInstance === this) SwipeRemove.openInstance = null;
        document.removeEventListener('touchstart', this.onDocumentTouch, true);
      }

      get actionWidth() {
        // Disc plus a 28px gutter — half sits on each side of the disc
        // (inset-inline-end in theme.css is the other half; keep in step).
        return this.action.offsetWidth + 28;
      }

      onTouchStart(event) {
        this.startX = event.changedTouches[0].clientX;
        this.startY = event.changedTouches[0].clientY;
        this.baseOffset = this.offset || 0;
        this.dragging = false;
        this.lastDx = 0;
        this.velocity = 0;
        this.content.classList.remove('is-settling');
        this.action.classList.remove('is-settling');
      }

      onTouchMove(event) {
        const dx = (event.changedTouches[0].clientX - this.startX) * this.rtl;
        const dy = event.changedTouches[0].clientY - this.startY;

        if (!this.dragging) {
          if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
          this.dragging = true;
        }

        this.velocity = dx - this.lastDx;
        this.lastDx = dx;

        // Negative offset = revealed. Rubber-band slightly past the disc,
        // never past zero.
        const max = this.actionWidth * 1.15;
        this.setOffset(Math.max(-max, Math.min(0, this.baseOffset + dx)));
      }

      onTouchEnd() {
        if (!this.dragging) return;
        this.dragging = false;

        const open = -this.offset > this.actionWidth * 0.5 || this.velocity < -6;
        this.settle(open ? -this.actionWidth : 0);
      }

      setOffset(offset) {
        this.offset = offset;
        this.content.style.transform = `translateX(${offset * this.rtl}px)`;
        this.style.setProperty('--swipe-progress', Math.min(1, -offset / this.actionWidth).toFixed(3));
      }

      settle(target) {
        this.content.classList.add('is-settling');
        this.action.classList.add('is-settling');
        this.setOffset(target);

        if (target !== 0) {
          if (SwipeRemove.openInstance && SwipeRemove.openInstance !== this) {
            SwipeRemove.openInstance.settle(0);
          }
          SwipeRemove.openInstance = this;
          this.action.setAttribute('tabindex', '0');
          this.action.setAttribute('aria-hidden', 'false');
          document.addEventListener('touchstart', this.onDocumentTouch, true);
        } else {
          if (SwipeRemove.openInstance === this) SwipeRemove.openInstance = null;
          this.action.setAttribute('tabindex', '-1');
          this.action.setAttribute('aria-hidden', 'true');
          document.removeEventListener('touchstart', this.onDocumentTouch, true);
        }
      }

      onDocumentTouch(event) {
        if (!this.contains(event.target)) this.settle(0);
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends HTMLAnchorElement {
      constructor() {
        super();

        this.addEventListener('click', (event) => {
          event.preventDefault();

          const cartItems = this.closest('cart-items');

          // Capture scroll BEFORE the collapse animation shrinks the list,
          // so the re-render restores the spot the removed row was at.
          // The scrollable is an ancestor of cart-items, so use closest().
          const scrollable = cartItems?.closest('.drawer__scrollable');
          if (scrollable) cartItems._pendingScrollTop = scrollable.scrollTop;

          const row = this.closest('.horizontal-product');
          if (row && !row.classList.contains('is-removing')) {
            // Pin the current height, then collapse it on the same frame the
            // class (padding/opacity/transform transitions) commits — one
            // synced motion, no stutter.
            row.style.height = `${row.offsetHeight}px`;
            row.classList.add('is-removing');
            void row.offsetHeight; // force reflow so the start state commits
            row.style.height = '0px';
          }

          cartItems.updateQuantity(this.getAttribute('data-index'), 0);
        });
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    class CartItems extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onChange(event) {
        this.validateQuantity(event);
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        if (typeof event.cart.sections === 'undefined') return;

        // If a removed row is still mid-collapse, let the animation finish
        // before swapping the DOM — otherwise the remaining rows snap the
        // last few pixels instead of sliding smoothly.
        const collapsingRow = document.querySelector(`#MiniCart-${this.sectionId} .horizontal-product.is-removing`);
        if (collapsingRow && collapsingRow.offsetHeight > 0 && !event._afterCollapse) {
          event._afterCollapse = true;
          let resumed = false;
          const resume = () => {
            if (resumed) return;
            resumed = true;
            this.onCartUpdate(event);
          };
          collapsingRow.addEventListener('transitionend', (e) => {
            if (e.propertyName === 'height') resume();
          }, { once: true });
          setTimeout(resume, 600);
          return;
        }

        const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');

        const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
        if (miniCart) {
          const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false
                cartDiscount.setAttribute('open', 'immediate');
                cartDiscount.setAttribute('active', '');
              }
            }
            const previousTotal = miniCart.querySelector('.checkout-total')?.textContent;
            const previousProgress = miniCart.querySelector('.free-shipping-bar progress-bar')?.style.getPropertyValue('--progress');
            // Prefer the value captured at remove-click (before the collapse
            // animation shrank the list); fall back to the live scrollTop for
            // quantity edits.
            const scrollable = miniCart.querySelector('.drawer__scrollable');
            const previousScrollTop = this._pendingScrollTop ?? (scrollable ? scrollable.scrollTop : 0);
            this._pendingScrollTop = undefined;

            this.preserveLoadedImages(miniCart, updatedElement);
            miniCart.innerHTML = updatedElement.innerHTML;

            // Stash for restore AFTER the focus-management block below — on a
            // removal that block focuses the first product title, which would
            // otherwise scroll the list to the top.
            this._restoreScrollTop = previousScrollTop;

            const totalElement = miniCart.querySelector('.checkout-total');
            if (totalElement && previousTotal !== undefined && totalElement.textContent !== previousTotal) {
              totalElement.classList.add('price-tick');
            }

            const progressBar = miniCart.querySelector('.free-shipping-bar progress-bar');
            if (progressBar && previousProgress && previousProgress !== progressBar.style.getPropertyValue('--progress')) {
              const targetProgress = progressBar.style.getPropertyValue('--progress');
              progressBar.style.setProperty('--progress', previousProgress);
              progressBar.offsetWidth;
              progressBar.style.setProperty('--progress', targetProgress);
            }
          }
        }

        const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
        if (mainCart) {
          const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false
                cartDiscount.setAttribute('open', '');
                cartDiscount.setAttribute('aria-expanded', 'true');
              }
            }
            
            const scrollTop = window.scrollY;
            this.preserveLoadedImages(mainCart, updatedElement);
            mainCart.innerHTML = updatedElement.innerHTML;

            if (event.source === 'cart-discount') {
              requestIdleCallback(() => {
                window.scrollTo({
                  top: scrollTop,
                  behavior: 'instant'
                });
              });
              
            }
          }
          else {
            mainCart.closest('.cart').classList.add('is-empty');
            mainCart.remove();
          }
        }

        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
        }
        else if (event.cart.item_count === 0) {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('a'))
            : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
        }
        else {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
            : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
        }

        // Restore scroll AFTER focus management (trapFocus above can scroll
        // the list to the top by focusing the first item). Keep the user where
        // they were; rows below a removed item simply slide up into place.
        if (this._restoreScrollTop > 0) {
          const scrollable = miniCart?.querySelector('.drawer__scrollable');
          if (scrollable) scrollable.scrollTo({ top: this._restoreScrollTop, behavior: 'instant' });
        }
        this._restoreScrollTop = undefined;

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: event.cart
          }
        }));
      }

      onCartError(errors, target) {
        // Removal failed — spring collapsed rows back
        this.querySelectorAll('.is-removing').forEach((row) => {
          row.classList.remove('is-removing');
          row.style.removeProperty('height');
        });

        if (target) {
          // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        }
        else {
          window.location.href = theme.routes.cart_url;
        }

        alert(errors);
      }

      updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        // Latest-wins queue: while a request is in flight, keep accepting
        // taps — only the newest desired quantity per line is sent once the
        // active request returns, and stale responses are never rendered.
        // State is static (shared) because the section re-render replaces
        // this element mid-session; a late debounced change must see the
        // same queue as the instance that started the request.
        const shared = this.constructor;
        shared._pendingUpdates ??= new Map();

        if (shared._activeRequest) {
          shared._pendingUpdates.set(line, { quantity, name, target });
          return;
        }

        this.sendUpdate(line, quantity, name, target);
      }

      sendUpdate(line, quantity, name, target) {
        const shared = this.constructor;
        shared._activeRequest = true;

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({
          id: line,
          quantity,
          sections: sectionsToBundle
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            shared._activeRequest = false;

            if (this.flushPendingUpdate()) return;

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
            this.clearLoaders();
          })
          .catch((error) => {
            shared._activeRequest = false;

            if (error.name === 'AbortError') {
            }
            else {
              console.error(error);
              if (!this.flushPendingUpdate()) this.clearLoaders();
            }
          });
      }

      flushPendingUpdate() {
        const shared = this.constructor;
        if (!shared._pendingUpdates || shared._pendingUpdates.size === 0) return false;

        const [nextLine, next] = shared._pendingUpdates.entries().next().value;
        shared._pendingUpdates.delete(nextLine);
        this.sendUpdate(nextLine, next.quantity, next.name, next.target);
        return true;
      }

      // Images the user is already seeing must not flash through the lazy
      // loading treatment again when the re-render rebuilds identical nodes.
      preserveLoadedImages(current, updated) {
        const loadedSrcs = new Set();
        current.querySelectorAll('img[is="lazy-image"]').forEach((img) => {
          if (img.classList.contains('loaded') || (img.complete && img.naturalWidth > 0)) {
            loadedSrcs.add(img.getAttribute('src'));
          }
        });
        updated.querySelectorAll('img[is="lazy-image"]:not(.loaded)').forEach((img) => {
          if (loadedSrcs.has(img.getAttribute('src'))) {
            img.classList.add('loaded');
            // Already in cache — fetch eagerly and decode synchronously so
            // the image paints on the same frame as the DOM swap (slower
            // mobile GPUs otherwise show a few blank frames while decoding).
            img.setAttribute('loading', 'eager');
            img.setAttribute('decoding', 'sync');
          }
        });
      }

      // The queue is idle — no spinner has a reason to live. The re-render
      // normally resets them, but any loader orphaned by a DOM swap mid-
      // request would otherwise spin forever.
      clearLoaders() {
        document.querySelectorAll(`[id^="Loader-${this.sectionId}-"]:not([hidden])`).forEach((loader) => {
          loader.hidden = true;
        });
      }

      enableLoading(line) {
        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;
      }

      disableLoading(line) {
        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = true;
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        }
        else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('main-cart')) {
  customElements.define(
    'main-cart',
    class MainCart extends HTMLElement {
      constructor() {
        super();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends HTMLElement {
      constructor() {
        super();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

        if (this.getAttribute('country') !== '') {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
        this.provinceElement.parentElement.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLFormElement {
      constructor() {
        super();

        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onSubmitHandler);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButton.removeEventListener('click', this.onSubmitHandler);
      }

      onSubmit(event) {
        event.preventDefault();

        this.abortController?.abort();
        this.abortController = new AbortController();

        const zip = this.querySelector('[name="address[zip]"]').value,
          country = this.querySelector('[name="address[country]"]').value,
          province = this.querySelector('[name="address[province]"]').value;

        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          shipping_address: { zip, country, province }
        });
        let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

        // remove double `/` in case shop might have /en or language in URL
        sectionUrl = sectionUrl.replace('//', '/');

        fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates);
            }
            else {
              this.formatError(parsedState);
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.resultsElement.hidden = false;
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      formatError(errors) {
        const shippingRatesList = Object.keys(errors).map((errorKey) => {
          return `<li>${errors[errorKey]}</li>`;
        });
        this.resultsElement.classList.remove('alert--success');
        this.resultsElement.classList.add('alert--error');
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${theme.shippingCalculatorStrings.error}</p>
          <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
        `;
      }

      formatShippingRates(shippingRates) {
        const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
          return `<li>${presentment_name}: ${currency} ${price}</li>`;
        });
        if (shippingRates.length) {
          this.resultsElement.classList.remove('alert--error');
          this.resultsElement.classList.add('alert--success');
        }
        else {
          this.resultsElement.classList.remove('alert--success');
          this.resultsElement.classList.add('alert--error');
        }
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
          ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
        `;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLFormElement {
      constructor() {
        super();

        this.onApplyDiscount = this.applyDiscount.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }
      
      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onApplyDiscount);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButton.removeEventListener('click', this.onApplyDiscount);
      }

      applyDiscount(event) {
        event.preventDefault();

        const discountCode = this.querySelector('[name="discount"]');
        if (!(discountCode instanceof HTMLInputElement) || typeof this.getAttribute('data-section-id') !== 'string') return;

        this.abortController?.abort();
        this.abortController = new AbortController();

        const discountCodeValue = discountCode.value.trim();
        if (discountCodeValue === '') return;

        const existingDiscounts = this.existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        this.setDiscountError('');
        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (
              parsedState.discount_codes.find((discount) => {
                return discount.code === discountCodeValue && discount.applicable === false;
              })
            ) {
              discountCode.value = '';
              this.setDiscountError(theme.discountStrings.error);
              return;
            }

            const newHtml = parsedState.sections[this.sectionId];
            const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
            const section = parsedHtml.getElementById(`shopify-section-${this.sectionId}`);
            if (section) {
              const discountCodes = section?.querySelectorAll('button[is="discount-remove"]') || [];
              const codes = Array.from(discountCodes)
                .map((element) => (element instanceof HTMLButtonElement ? element.getAttribute('data-discount') : null))
                .filter(Boolean);

              if (
                codes.length === existingDiscounts.length &&
                codes.every((code) => existingDiscounts.includes(code)) &&
                parsedState.discount_codes.find((discount) => {
                  return discount.code === discountCodeValue && discount.applicable === true;
                })
              ) {
                discountCode.value = '';
                this.setDiscountError(theme.discountStrings.shippingError);
                return;
              }
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      removeDiscount(event) {
        if ((event instanceof KeyboardEvent && event.key !== 'Enter') || !(event instanceof MouseEvent)) {
          return;
        }

        const discountCode = event.target.getAttribute('data-discount');
        if (!discountCode) return;

        const existingDiscounts = this.existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setDiscountError('');
        event.target.setAttribute('loading', '');

        const body = JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            event.target.removeAttribute('loading');
          });
      }

      existingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll('button[is="discount-remove"]');
        for (const pill of discountPills) {
          if (pill.hasAttribute('data-discount')) {
            discountCodes.push(pill.getAttribute('data-discount'));
          }
        }
        return discountCodes;
      }

      setDiscountError(error) {
        this.resultsElement.lastElementChild.textContent = error;
        this.resultsElement.hidden = error.length === 0;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('discount-remove')) {
  customElements.define(
    'discount-remove',
    class DiscountRemove extends MagnetButton {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="cart-discount"]') || document.querySelector('form[is="cart-discount"]');

        if (form) {
          event.preventDefault();
          form.removeDiscount(event);
        }
      }
    }, { extends: 'button' }
  );
}
